import { useMemo, useState } from 'react';

import type { TraceSpanResponse } from '../../lib/api-client';

/**
 * Lightweight SigNoz-style trace waterfall: span rows on the left,
 * proportional time bars on a shared axis, click a span for its attributes.
 * Pure component — data arrives normalized from the agent-server proxy.
 *
 * Two orderings, because they answer different questions and neither is a
 * substitute for the other:
 *
 * - **Grouped** — parent-child, children by start time. Answers "what caused
 *   what": which DNS lookup belongs to which dial, which relay to which turn.
 * - **Chronological** — every span flat, by start time. Answers "what actually
 *   happened when". Nesting reorders the page (a deep child sorts under its
 *   parent, far from siblings that ran at the same moment), so a real
 *   ordering question is unanswerable in the grouped view.
 *
 * Rows are flat in chronological mode ON PURPOSE: keeping the tree indent
 * while breaking the tree order would draw a hierarchy that the row order
 * contradicts.
 */

interface SpanNode extends TraceSpanResponse {
  depth: number;
  children: SpanNode[];
}

/**
 * Service colors are claimed IN ORDER OF FIRST APPEARANCE in the rendered
 * (root-first) span list: the first service takes color 1, the second color
 * 2, and so on — deterministic per trace, and stable across traces whose
 * services appear in the same order (the root's service always gets color
 * 1). Palette is hand-ordered so ADJACENT claims are maximally distinct
 * (hue-rotating, no near-neighbors); pure red is deliberately absent — red
 * fill is reserved for error spans.
 */
const SERVICE_PALETTE = [
  '#3b82f6', // 1 blue
  '#f59e0b', // 2 amber
  '#10b981', // 3 emerald
  '#8b5cf6', // 4 violet
  '#06b6d4', // 5 cyan
  '#d946ef', // 6 fuchsia
  '#84cc16', // 7 lime
  '#f97316', // 8 orange
  '#6366f1', // 9 indigo
  '#14b8a6', // 10 teal
  '#eab308', // 11 yellow
  '#0ea5e9', // 12 sky
  '#a855f7', // 13 purple
  '#22c55e', // 14 green
  '#c026d3', // 15 magenta
  '#78716c', // 16 stone
];

/** service → claimed color, by first appearance in the ordered span list. */
function claimServiceColors(ordered: SpanNode[]): Map<string, string> {
  const colors = new Map<string, string>();
  for (const span of ordered) {
    if (!colors.has(span.service)) {
      colors.set(span.service, SERVICE_PALETTE[colors.size % SERVICE_PALETTE.length]!);
    }
  }
  return colors;
}

function buildTree(spans: TraceSpanResponse[]): SpanNode[] {
  const nodes = new Map<string, SpanNode>();
  for (const span of spans) nodes.set(span.span_id, { ...span, depth: 0, children: [] });
  const roots: SpanNode[] = [];
  for (const node of nodes.values()) {
    const parent = node.parent_span_id ? nodes.get(node.parent_span_id) : undefined;
    if (parent) parent.children.push(node);
    else roots.push(node);
  }
  const ordered: SpanNode[] = [];
  const visit = (node: SpanNode, depth: number) => {
    node.depth = depth;
    ordered.push(node);
    node.children.sort((a, b) => a.start_ns - b.start_ns);
    for (const child of node.children) visit(child, depth + 1);
  };
  roots.sort((a, b) => a.start_ns - b.start_ns);
  for (const root of roots) visit(root, 0);
  return ordered;
}

/** Every span, flat, in start order. Ties break on span id so the order is
 *  stable across renders rather than dependent on map-iteration order. */
function buildChronological(spans: TraceSpanResponse[]): SpanNode[] {
  return spans
    .map((span) => ({ ...span, depth: 0, children: [] as SpanNode[] }))
    .sort((a, b) => a.start_ns - b.start_ns || a.span_id.localeCompare(b.span_id));
}

type TraceView = 'grouped' | 'chronological';

function fmtNs(ns: number): string {
  if (ns < 1_000_000) return `${(ns / 1_000).toFixed(0)}µs`;
  if (ns < 1_000_000_000) return `${(ns / 1_000_000).toFixed(1)}ms`;
  return `${(ns / 1_000_000_000).toFixed(2)}s`;
}

export function TraceWaterfall({ spans }: { spans: TraceSpanResponse[] }) {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [view, setView] = useState<TraceView>('grouped');

  const { grouped, chronological, minStart, total, serviceColors } = useMemo(() => {
    const groupedNodes = buildTree(spans);
    const min = Math.min(...spans.map((s) => s.start_ns));
    const max = Math.max(...spans.map((s) => s.start_ns + s.duration_ns));
    return {
      grouped: groupedNodes,
      chronological: buildChronological(spans),
      minStart: min,
      total: Math.max(1, max - min),
      // Claimed from the GROUPED order in both views, so a service keeps its
      // colour when you toggle. Claiming per-view would repaint the trace on
      // every switch and make the two views hard to compare — which is the
      // one thing having both is for.
      serviceColors: claimServiceColors(groupedNodes),
    };
  }, [spans]);
  const ordered = view === 'grouped' ? grouped : chronological;
  const colorOf = (service: string): string =>
    serviceColors.get(service) ?? SERVICE_PALETTE[0]!;

  const selected = selectedId ? (ordered.find((s) => s.span_id === selectedId) ?? null) : null;

  if (spans.length === 0) {
    return (
      <div style={{ padding: 24, fontSize: 12, color: 'var(--text-mute)' }}>
        No spans in this trace.
      </div>
    );
  }

  const ticks = [0, 0.25, 0.5, 0.75, 1];

  return (
    <div style={{ display: 'flex', height: '100%', minHeight: 0 }}>
      <div style={{ flex: 1, minWidth: 0, overflow: 'auto' }}>
        {/* Time axis */}
        <div
          style={{
            display: 'flex',
            position: 'sticky',
            top: 0,
            background: 'var(--bg)',
            borderBottom: '1px solid var(--border)',
            zIndex: 1,
          }}
        >
          <div
            style={{
              width: 300,
              flexShrink: 0,
              padding: '6px 10px',
              fontSize: 10,
              color: 'var(--text-mute)',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              flexWrap: 'wrap',
            }}
          >
            <span>
              {ordered.length} spans · {fmtNs(total)}
            </span>
            <span
              role="group"
              aria-label="Span ordering"
              style={{ display: 'inline-flex', border: '1px solid var(--border)', borderRadius: 4, overflow: 'hidden' }}
            >
              {(
                [
                  ['grouped', 'Grouped', 'Parent-child: which span caused which'],
                  ['chronological', 'Chronological', 'Flat, by start time: what happened when'],
                ] as const
              ).map(([value, label, hint]) => (
                <button
                  key={value}
                  type="button"
                  title={hint}
                  aria-pressed={view === value}
                  onClick={() => setView(value)}
                  style={{
                    font: 'inherit',
                    fontSize: 10,
                    lineHeight: 1,
                    padding: '3px 7px',
                    border: 'none',
                    cursor: 'pointer',
                    background: view === value ? 'var(--accent, #3b82f6)' : 'transparent',
                    color: view === value ? '#fff' : 'var(--text-mute)',
                  }}
                >
                  {label}
                </button>
              ))}
            </span>
            {[...serviceColors.entries()].map(([service, color]) => (
              <span
                key={service}
                title={service}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 3, minWidth: 0 }}
              >
                <span
                  data-testid="service-swatch"
                  data-service={service}
                  style={{
                    width: 7,
                    height: 7,
                    borderRadius: 2,
                    background: color,
                    flexShrink: 0,
                  }}
                />
                <span
                  style={{
                    maxWidth: 110,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {service}
                </span>
              </span>
            ))}
          </div>
          <div style={{ flex: 1, position: 'relative', minWidth: 240 }}>
            {ticks.map((t) => (
              <span
                key={t}
                style={{
                  position: 'absolute',
                  left: `${t * 100}%`,
                  transform: t === 1 ? 'translateX(-100%)' : undefined,
                  fontSize: 9,
                  color: 'var(--text-mute)',
                  padding: '6px 2px',
                }}
              >
                {fmtNs(total * t)}
              </span>
            ))}
          </div>
        </div>
        {/* Span rows */}
        {ordered.map((span) => {
          const left = ((span.start_ns - minStart) / total) * 100;
          const width = Math.max(0.4, (span.duration_ns / total) * 100);
          const failed = span.status.toLowerCase() === 'error';
          const isSelected = span.span_id === selectedId;
          return (
            <div
              key={span.span_id}
              data-testid="span-row"
              onClick={() => setSelectedId(isSelected ? null : span.span_id)}
              style={{
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
                background: isSelected ? 'var(--bg-alt)' : 'transparent',
                borderBottom: '1px solid var(--border)',
              }}
            >
              <div
                style={{
                  width: 300,
                  flexShrink: 0,
                  padding: `5px 10px 5px ${10 + span.depth * 14}px`,
                  fontSize: 11,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
                title={`${span.name} (${span.service})`}
              >
                <span
                  data-testid="span-name"
                  style={{ color: failed ? 'var(--kind-failure)' : 'var(--text)' }}
                >
                  {span.name}
                </span>
                <span
                  style={{
                    display: 'inline-block',
                    width: 7,
                    height: 7,
                    borderRadius: 2,
                    background: colorOf(span.service),
                    marginLeft: 6,
                    marginRight: 4,
                    verticalAlign: 'baseline',
                  }}
                />
                <span style={{ color: 'var(--text-mute)', fontSize: 10 }}>{span.service}</span>
              </div>
              <div style={{ flex: 1, position: 'relative', height: 22, minWidth: 240 }}>
                <div
                  style={{
                    position: 'absolute',
                    left: `${left}%`,
                    width: `${width}%`,
                    top: 5,
                    height: 12,
                    borderRadius: 3,
                    background: failed ? 'var(--kind-failure, #cf222e)' : colorOf(span.service),
                    opacity: isSelected ? 1 : 0.9,
                  }}
                />
                <span
                  style={{
                    position: 'absolute',
                    left: `${Math.min(88, left + width + 0.6)}%`,
                    top: 4,
                    fontSize: 9,
                    color: 'var(--text-mute)',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {fmtNs(span.duration_ns)}
                </span>
              </div>
            </div>
          );
        })}
      </div>
      {/* Span detail side panel — every field the SigNoz wire carries,
          grouped into collapsible sections. */}
      {selected ? (
        <div
          style={{
            width: 340,
            flexShrink: 0,
            borderLeft: '1px solid var(--border)',
            overflow: 'auto',
            padding: '12px 14px',
            background: 'var(--surface)',
          }}
        >
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)', marginBottom: 2 }}>
            {selected.name}
          </div>
          <div
            style={{
              fontSize: 10,
              color: 'var(--text-mute)',
              marginBottom: 10,
              display: 'flex',
              alignItems: 'center',
              gap: 4,
            }}
          >
            <span
              style={{
                width: 7,
                height: 7,
                borderRadius: 2,
                background: colorOf(selected.service),
                flexShrink: 0,
              }}
            />
            {selected.service} · {fmtNs(selected.duration_ns)} · {selected.status || 'unset'}
          </div>

          <Section title="Span" count={null} defaultOpen>
            <dl style={{ margin: 0, fontSize: 11 }}>
              <Field label="span_id" value={selected.span_id} />
              <Field label="trace_id" value={selected.trace_id ?? '—'} />
              <Field label="parent_span_id" value={selected.parent_span_id ?? '(root)'} />
              <Field label="service" value={selected.service} />
              <Field label="kind" value={selected.span_kind || selected.kind || '—'} />
              <Field label="start +" value={fmtNs(selected.start_ns - minStart)} />
              <Field label="duration_nano" value={String(selected.duration_ns)} />
              <Field label="has_error" value={String(selected.has_error ?? false)} />
              <Field label="status_code_string" value={selected.status_code_string || 'Unset'} />
              {selected.status_message ? (
                <Field label="status_message" value={selected.status_message} />
              ) : null}
            </dl>
          </Section>

          <Section title="Attributes" count={Object.keys(selected.attributes).length} defaultOpen>
            {Object.keys(selected.attributes).length === 0 ? (
              <Empty>no attributes</Empty>
            ) : (
              <dl style={{ margin: 0, fontSize: 11 }}>
                {Object.entries(selected.attributes).map(([k, v]) => (
                  <Field key={k} label={k} value={v} />
                ))}
              </dl>
            )}
          </Section>

          <Section title="Events" count={(selected.events ?? []).length}>
            {(selected.events ?? []).length === 0 ? (
              <Empty>no events</Empty>
            ) : (
              (selected.events ?? []).map((ev, i) => (
                <pre
                  key={i}
                  style={{
                    margin: '0 0 8px',
                    padding: '6px 8px',
                    fontSize: 10,
                    fontFamily: 'ui-monospace, SFMono-Regular, monospace',
                    background: 'var(--bg-alt)',
                    border: '1px solid var(--border)',
                    borderRadius: 4,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-all',
                  }}
                >
                  {typeof ev === 'string' ? ev : JSON.stringify(ev, null, 2)}
                </pre>
              ))
            )}
          </Section>

          <Section title="References" count={(selected.references ?? []).length}>
            {(selected.references ?? []).length === 0 ? (
              <Empty>no references</Empty>
            ) : (
              (selected.references ?? []).map((ref, i) => (
                <dl key={i} style={{ margin: '0 0 8px', fontSize: 11 }}>
                  <Field label="refType" value={ref.ref_type || '—'} />
                  <Field label="spanId" value={ref.span_id || '—'} />
                  <Field label="traceId" value={ref.trace_id || '—'} />
                </dl>
              ))
            )}
          </Section>
        </div>
      ) : null}
    </div>
  );
}

function Section({
  title,
  count,
  defaultOpen = false,
  children,
}: {
  title: string;
  count: number | null;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  return (
    <details open={defaultOpen} style={{ marginBottom: 10 }}>
      <summary
        style={{
          cursor: 'pointer',
          fontSize: 10,
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: 0.5,
          color: 'var(--text-soft)',
          padding: '4px 0',
          borderBottom: '1px solid var(--border)',
          marginBottom: 8,
          userSelect: 'none',
        }}
      >
        {title}
        {count !== null ? (
          <span style={{ color: 'var(--text-mute)', fontWeight: 400, marginLeft: 6 }}>
            {count}
          </span>
        ) : null}
      </summary>
      {children}
    </details>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return <div style={{ fontSize: 10.5, color: 'var(--text-mute)', paddingBottom: 4 }}>{children}</div>;
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ marginBottom: 8, minWidth: 0 }}>
      <dt style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--text-mute)' }}>
        {label}
      </dt>
      <dd
        style={{
          margin: 0,
          fontFamily: 'ui-monospace, SFMono-Regular, monospace',
          fontSize: 10.5,
          color: 'var(--text)',
          wordBreak: 'break-all',
          whiteSpace: 'pre-wrap',
        }}
      >
        {value}
      </dd>
    </div>
  );
}
